import React, { FC, useEffect, useState } from "react";
import Select from "react-select";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

// UI
import InputComponent from "Components/UI/Input";
import LabelComponent from "Components/UI/Label";
import ButtonComponent from "Components/UI/Button";
import CommonErrorMessage from "Components/UI/CommonErrorMessage";
import FieldErrorMessage from "Components/UI/FeildErrorMessage";

// Email Domain
import RestrictedEmailDomainList from "StaticData/email_domains.js";

// Utils
import { validateEmail, isValidUrl } from "Library/Utils";

import {
  getLatLongFromAddress,
  getTimeZone,
} from "Network/Core/GoogleAPI/GoogleAPI";

// css
import "Assets/css/style.css";
import "Assets/css/common.css";
import "Assets/css/booking-list.css";
import "Assets/css/booking-form.css";
import "Assets/css/site-management.css";
import "Assets/css/global-admin.css";
import "Assets/css/cutstmize.css";

//Helper
import { useDashboardSettings } from "Hooks/useDashboardSettings";
import { useApiCall } from "Hooks/useApiCall";
import { usePermissions } from "Hooks/usePermissions";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { createProject } from "Network/Core/Projects";
import { getUserByEmail } from "Network/Core/Users";
import { routes } from "Library/Constants";
import toast from "react-hot-toast";
import { ModalBody, ModalHeader, ModalWrapper } from ".";

export interface ProjectCreateModalProps {
  open: boolean;
  onClose: () => void;
}

const ProjectCreateModal: FC<ProjectCreateModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { call } = useApiCall();

  const navigate = useNavigate();
  const { regions, selectedPlatform } = useDashboardSettings();

  // An org admin can only create projects for their own organization. We resolve the
  // owner email to its organization and notify / block accordingly. Global admins are
  // unrestricted, so the check is skipped for them.
  const { isOrgAdmin, isGlobalAdmin } = usePermissions();
  const myOrg: any = useSelector(
    (state: RootState) => state.auth.userData?.organization
  );

  const regionsOptions = regions?.map((el) => ({
    value: el.id,
    label: el.name,
  }));

  const [isDisable, setIsDisable] = useState<any>(true);
  const [regionSelected, setRegionSelected] = useState<any>(false);
  const [ownerOrgNotice, setOwnerOrgNotice] = useState<{
    type: "info" | "error";
    message: string;
  } | null>(null);

  // UseForm
  const {
    register, // this use for events and value
    handleSubmit, // this is form submit handle
    setError,
    getValues,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm();

  const getTimeZoneFromAddress = async (address: string) => {
    const res = await getLatLongFromAddress(address);
    const country = res.data.results[0]?.address_components.find(
      (component: any) => component.types.includes("country")
    );
    const timeZone = await getTimeZone(
      res.data.results[0]?.geometry.location.lat,
      res.data.results[0]?.geometry.location.lng
    );

    return {
      timeZoneId: timeZone.data.timeZoneId,
      country,
    };
  };

  const handleSubmitProjectDetail = async (projectData: any) => {
    const { timeZoneId, country } = await getTimeZoneFromAddress(projectData.siteAddress);
    setIsDisable(true);
    const payloadData = {
      name: projectData.siteName,
      address: projectData.siteAddress,
      hubspot_url: projectData.hubSpot,
      owner_email: projectData.email,
      region: projectData.region,
      timezone: timeZoneId,
      platform: selectedPlatform?.code,
      country: `${country?.short_name}`,
    };
    call(
      () => createProject(payloadData),
      (data) => {
        navigate(routes.dashboardNav.project(data.ref));
        toast.success(t("Translation.created_project"));
        setIsDisable(false);
        onClose();
      },
      () => {
        setIsDisable(false);
      }
    );
  };

  const changeRegion = (value: { label: string; value: number }) => {
    setValue("region", value.value);
    setRegionSelected(true);

    if (!value.value) {
      setIsDisable(true);
    } else if (
      getValues("siteAddress") !== "" &&
      getValues("siteName") !== "" &&
      getValues("email") !== "" &&
      Object.keys(errors).length <= 0
    ) {
      setIsDisable(false);
    }
  };

  const onChangeInput = (e: any) => {
    // Editing the owner email invalidates any previous org resolution.
    if (e.target.name === "email") {
      setOwnerOrgNotice(null);
    }
    setIsDisable(
      getValues("siteAddress") === "" ||
        getValues("siteName") === "" ||
        getValues("email") === ""
    );
    setIsDisable(true);
    const { name, value } = e.target;
    if (value === "") {
      switch (name) {
        case "siteName":
          setError(name, {
            type: "required",
            message: t("Translation.error_sitename_required"),
          });
          setIsDisable(true);
          break;
        case "siteAddress":
          setError(name, {
            type: "required",
            message: t("Translation.error_siteadd_required"),
          });
          setIsDisable(true);
          break;
        case "email":
          setError(name, {
            type: "required",
            message: t("Translation.error_email_required"),
          });
          setIsDisable(true);
          break;
        case "hubSpot":
          clearErrors(name);
          if (
            getValues("siteName") !== "" &&
            getValues("siteAddress") !== "" &&
            getValues("email") !== "" &&
            Object.keys(errors).length <= 0 &&
            regionSelected !== false
          ) {
            setIsDisable(false);
          }
          break;
        default:
          setIsDisable(true);
          break;
      }
    } else {
      switch (name) {
        case "hubSpot":
          if (value !== "") {
            if (!isValidUrl(value)) {
              setIsDisable(true);
              setError(name, {
                type: "validate",
                message: t("Translation.invalid_url"),
              });
            } else {
              clearErrors(name);
              if (
                getValues("siteName") !== "" &&
                getValues("siteAddress") !== "" &&
                getValues("email") !== "" &&
                Object.keys(errors).length <= 0 &&
                regionSelected !== false
              ) {
                setIsDisable(false);
              }
            }
          } else {
            clearErrors(name);
            if (
              getValues("siteName") !== "" &&
              getValues("siteAddress") !== "" &&
              getValues("email") !== "" &&
              Object.keys(errors).length <= 0 &&
              regionSelected !== false
            ) {
              setIsDisable(false);
            }
          }

          break;
        case "siteName":
          clearErrors(name);
          if (
            getValues("siteAddress") !== "" &&
            getValues("email") !== "" &&
            Object.keys(errors).length <= 0 &&
            regionSelected !== false
          ) {
            setIsDisable(false);
          }
          break;
        case "siteAddress":
          clearErrors(name);

          if (
            getValues("siteName") !== "" &&
            getValues("email") !== "" &&
            Object.keys(errors).length <= 0 &&
            regionSelected !== false
          ) {
            setIsDisable(false);
          }
          break;
        default:
          clearErrors(name);
          break;
      }
    }
  };

  // Resolve the owner email to its organization. Org admins may only create projects
  // for their own org, so a cross-org owner blocks the create; a same-org or brand-new
  // owner is allowed and the project will belong to the org admin's own organization.
  const resolveOwnerOrg = async (email: string) => {
    if (!isOrgAdmin || isGlobalAdmin || !myOrg) {
      setOwnerOrgNotice(null);
      return true;
    }

    const res: any = await getUserByEmail(email);
    const ownerOrg = res?.status === 200 ? res?.data?.organization : null;

    // Existing owner in a different org -> not allowed.
    if (ownerOrg?.ref && ownerOrg.ref !== myOrg.ref) {
      setOwnerOrgNotice({
        type: "error",
        message: t("Translation.project_create_owner_cross_org", {
          org: myOrg.name,
          ownerOrg: ownerOrg.name,
        }),
      });
      return false;
    }

    // Same-org owner or a brand-new email -> project is created under the admin's org.
    setOwnerOrgNotice({
      type: "info",
      message: t("Translation.project_create_under_org", { org: myOrg.name }),
    });
    return true;
  };

  const onBlurEmail = async (e: any) => {
    const { name, value } = e.target;
    if (value === "") {
      setOwnerOrgNotice(null);
      setError(name, {
        type: "validate",
        message: t("Translation.error_email_required"),
      });
      setIsDisable(true);
    } else {
      if (!validateEmail(value)) {
        setOwnerOrgNotice(null);
        setError(name, {
          type: "validate",
          message: t("Translation.error_valid_email"),
        });
        setIsDisable(true);
      } else if (
        RestrictedEmailDomainList.find(
          (item) =>
            value.substring(value.indexOf("@") + 1, value.length) === item
        )
      ) {
        setOwnerOrgNotice(null);
        setIsDisable(true);
        setError(name, {
          type: "validate",
          message: t("Translation.error_valid_email"),
        });
      } else {
        clearErrors(name);
        const ownerOrgAllowed = await resolveOwnerOrg(value);
        if (!ownerOrgAllowed) {
          setIsDisable(true);
        } else if (
          getValues("siteAddress") !== "" &&
          getValues("siteName") !== "" &&
          Object.keys(errors).length <= 0 &&
          regionSelected !== false
        ) {
          setIsDisable(false);
        }
      }
    }
  };

  useEffect(() => {
    setValue("siteAddress", "");
    setValue("siteName", "");
    setValue("email", "");
    setValue("region", "");
    setRegionSelected(false);
  }, []);

  useEffect(() => {
    clearErrors();
    setValue("siteName", "");
    setValue("siteAddress", "");
    setValue("hubSpot", "");
    setValue("email", "");
    setValue("region", "");
    setOwnerOrgNotice(null);
  }, [open]);

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <ModalHeader title={t("Translation.create_project")} onClose={onClose} />
      <ModalBody>
        <div>
          <div className="form-group">
            <LabelComponent label={t("Translation.label_sitename") + "*"} />

            <InputComponent
              inputType="text"
              className={
                errors.siteName ? "form-control error" : "form-control"
              }
              inputName="siteName"
              register={register}
              rules={{
                required: {
                  value: true,
                  message: `${t("Translation.error_sitename_required")}`,
                },
                onChange: (e: any) => onChangeInput(e),
              }}
              id="siteName"
            />
            <FieldErrorMessage
              errors={errors}
              name="siteName"
              containerClass="w-100"
            />
          </div>
          <div className="form-group">
            <LabelComponent label={t("Translation.label_region") + "*"} />
            <Controller
              control={control}
              name="region"
              render={({ field: { value } }: any) => (
                <Select
                  classNamePrefix="form-control-language"
                  options={regionsOptions}
                  name="region"
                  value={regionsOptions?.filter((c: any) => c.value === value)}
                  onChange={(val: any) => {
                    changeRegion(val);
                  }}
                  placeholder={t("Translation.select")}
                  isSearchable
                />
              )}
            />
            <FieldErrorMessage
              errors={errors}
              name="region"
              containerClass="w-100"
            />
          </div>

          <div className="form-group">
            <LabelComponent label={t("Translation.label_siteadd") + "*"} />

            <InputComponent
              inputType="text"
              className={
                errors.siteAddress ? "form-control error" : "form-control"
              }
              inputName="siteAddress"
              register={register}
              rules={{
                required: {
                  value: true,
                  message: `${t("Translation.error_siteadd_required")}`,
                },
                onChange: (e: any) => onChangeInput(e),
              }}
              id="siteAddress"
            />
            <FieldErrorMessage
              errors={errors}
              name="siteAddress"
              containerClass="w-100"
            />
          </div>

          <div className="form-group">
            <LabelComponent
              label={t("Translation.project_owner_email_address") + "*"}
            />

            <InputComponent
              inputType="text"
              className={
                errors.siteName ? "form-control error" : "form-control"
              }
              inputName="email"
              register={register}
              rules={{
                required: {
                  value: true,
                  message: `${t("Translation.error_email_required")}`,
                },
                validate: (value: string) => {
                  return (
                    validateEmail(value) ||
                    `${t("Translation.error_valid_email")}`
                  );
                },
                onChange: (e: any) => onChangeInput(e),
                onBlur: (e: any) => onBlurEmail(e),
              }}
              id="email"
            />
            <FieldErrorMessage
              errors={errors}
              name="email"
              containerClass="w-100"
            />
            {ownerOrgNotice && (
              <p
                className="w-100"
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  color:
                    ownerOrgNotice.type === "error" ? "#d32f2f" : "#2e7d32",
                }}
              >
                {ownerOrgNotice.message}
              </p>
            )}
          </div>

          <div className="form-group">
            <LabelComponent label={t("Translation.label_hubspot")} />

            <InputComponent
              inputType="url"
              className={
                errors.siteName ? "form-control error" : "form-control"
              }
              inputName="hubSpot"
              register={register}
              rules={{
                onChange: (e: any) => onChangeInput(e),
              }}
              id="hubSpot"
            />
            <FieldErrorMessage
              errors={errors}
              name="hubSpot"
              containerClass="w-100"
            />
          </div>

          <CommonErrorMessage containerClass="w-100 login_error" />
        </div>
        <div className="mt-4">
          <ButtonComponent
            buttonLabel={t("Translation.create_project")}
            handleClick={handleSubmit(handleSubmitProjectDetail)}
            className="theme-btn w-100"
            disabled={isDisable}
          />
        </div>
      </ModalBody>
    </ModalWrapper>
  );
};

export default ProjectCreateModal;
